<template>
  <SignInSignUpLayout>
    <b-form @submit="signUp" class="sing-in-form">
      <h1 class="sign-in-title">Welcome</h1>

      <Description>
        Please fill in the following fields to start using your new account
        and join
        <template v-if="invite_agency_name">
          <span class="agency-name">{{ invite_agency_name }}</span> in
        </template>
        Easy Agency.
      </Description>

      <b-form-group>
        <label for="email-input" class="form__label input-label">Email</label>

        <b-form-input
          id="email-input"
          v-model="email"
          placeholder="Enter email"
          autocomplete="off"
          class="sing-up-input"
          disabled
        />
      </b-form-group>

      <UserNameField
        ref="userNameComponent"
        :initial_name="invite_name"
        :serverErrors="nameServerErrors"
        :hideValidationErrors="!submitButtonPressed"
      />

      <PasswordField
        v-model="password"
        id="password"
        label="Password"
        placeholder="Enter password"
        autocomplete="new-password"
        :state="passwordState"
      >
        <template #invalid-feedback>
          <div v-for="(error, index) in passwordServerErrors" :key="index">
            {{ error }}
          </div>
        </template>
      </PasswordField>

      <PasswordRequirements
        class="password-requirements-container"
        :hideStatuses="!submitButtonPressed"
        :hasMinLength="passwordValidationStatus.hasMinLength"
        :hasUppercaseAndLowercase="passwordValidationStatus.hasUppercase && passwordValidationStatus.hasLowercase"
        :hasNumbers="passwordValidationStatus.hasNumbers"
        :hasSpecialChars="passwordValidationStatus.hasSpecialChars"
      />

      <TermsOfUseCheckbox ref="termsOfUseCheckboxComponent" />

      <b-button class="button" type="submit" variant="primary" :disabled="isSubmitButtonDisabled">
        Confirm
      </b-button>
    </b-form>
  </SignInSignUpLayout>
</template>

<script>
import { validationMixin } from 'vuelidate'
import SignInSignUpLayout from '@components/sign-up/SignInSignUpLayout.vue'
import PasswordRequirements from '@components/sign-up/PasswordRequirements.vue'
import PasswordField from '@components/sign-up/PasswordField.vue'
import UserNameField from '@components/sign-up/complete-signup-progress/UserNameField.vue'
import { validatePassword } from '@components/sign-up/validatePassword'
import Description from '@components/sign-up/Description.vue'
import TermsOfUseCheckbox from '@components/sign-up/TermsOfUseCheckbox.vue'

export default {
  name: 'Welcome',
  mixins: [validationMixin],
  components: { TermsOfUseCheckbox, Description, UserNameField, SignInSignUpLayout, PasswordField, PasswordRequirements },
  data () {
    return {
      email: decodeURIComponent(this.$cookies.get('invite_email')),
      invite_name: this.$cookies.get('invite_name') ? decodeURIComponent(this.$cookies.get('invite_name')) : '',
      password: '',
      termsOfUse: false,
      invite_agency_name: decodeURIComponent(this.$cookies.get('invite_agency_name')),

      nameServerErrors: null,
      passwordServerErrors: null,

      submitButtonPressed: false,
      submitRequestPending: false
    }
  },
  validations: {
    password: {
      validPassword (password) {
        return validatePassword(password).isValid
      }
    }
  },
  computed: {
    passwordState () {
      if (!this.passwordServerErrors) {
        return null
      }

      return this.passwordServerErrors || this.$v.password.$invalid ? false : null
    },
    passwordValidationStatus () {
      return validatePassword(this.password)
    },
    isFormValid () {
      return !this.$v.$invalid && !this.$refs.userNameComponent.$v.$invalid && !this.$refs.termsOfUseCheckboxComponent.$v.$invalid
    },
    isSubmitButtonDisabled () {
      return this.submitRequestPending || (this.submitButtonPressed && !this.isFormValid)
    }
  },
  methods: {
    touchForm () {
      this.$v.$touch()
      this.$refs.userNameComponent.$v.$touch()
      this.$refs.termsOfUseCheckboxComponent.$v.$touch()
    },
    signUp (e) {
      e.preventDefault()

      this.submitButtonPressed = true
      this.nameServerErrors = null
      this.passwordServerErrors = null

      this.touchForm()
      if (!this.isFormValid) {
        return
      }

      this.submitRequestPending = true

      this.axios
        .post('/api/register/user-signup', {
          name: this.$refs.userNameComponent.name,
          password: this.password
        })
        .then(async (response) => {
          const user = response.data.user.original
          await this.authUserAtFrontEnd(response.data.access_token, response.data.refresh_token, user, user.roles)

          this.$router.push({ name: 'home' })
        })
        .catch((error) => {
          if (error.response?.data?.errors) {
            const { name, password, ...restErrors } = error.response.data.errors

            if (name) this.nameServerErrors = name
            if (password) this.passwordServerErrors = password

            const errors = Object.values(restErrors)
            for (let i = 0; i < errors.length; i++) {
              this.$snotify.error(
                errors[i][0],
                'Error',
                this.$store.state.snotify
              )
            }
          } else if (error) {
            this.$snotify.error(
              error,
              'Error',
              this.$store.state.snotify
            )
          }
        })
        .finally(() => {
          this.submitRequestPending = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.sign-in-title {
  margin-bottom: 10px !important;
}

.agency-name {
  color: $fontColorAccent;
  font-weight: 700;
}

.password-requirements-container {
  margin-bottom: 26px;
}
</style>
